import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AccessibilityPanel.css';

const LS_KEY = 'irbis_a11y';

const DEFAULT = {
  fontSize:      0,
  dyslexicFont:  false,
  grayscale:     false,
  largeCursor:   false,
  readingGuide:  false,
  letterSpacing: 0,
  zoom:          0,
  invertMode:    0,
  textColor:     '',
  bgColor:       '',
};

const FONT_VARS    = ['--font-size-xs','--font-size-sm','--font-size-md',
                      '--font-size-base','--font-size-lg','--font-size-xl'];
const FONT_BY_STEP = {
  '-2': ['10pt','11pt','12pt','13pt','14pt','15pt'],
  '-1': ['11pt','12pt','13pt','14pt','15pt','16pt'],
   '0': null,
   '1': ['13pt','14pt','15pt','17pt','19pt','21pt'],
   '2': ['14pt','15pt','16pt','19pt','21pt','23pt'],
   '3': ['15pt','16pt','18pt','21pt','23pt','25pt'],
};
const ZOOM_LEVELS  = [0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.35];

function load() {
  try { const r = localStorage.getItem(LS_KEY); if (r) return { ...DEFAULT, ...JSON.parse(r) }; }
  catch (_) {}
  return { ...DEFAULT };
}

function applySettings(s) {
  const root = document.documentElement;

  // Розмір шрифту — переопределяем CSS-переменные проекта
  const sizes = FONT_BY_STEP[String(s.fontSize)];
  if (sizes) {
    FONT_VARS.forEach((v, i) => root.style.setProperty(v, sizes[i]));
  } else {
    FONT_VARS.forEach(v => root.style.removeProperty(v));
  }

  // Масштаб сторінки — на .a11y-invert-scope, НЕ на body: інакше зумований
  // контекст body спотворює «Лінію читання» (JS ставить style.top = e.clientY,
  // а зум множить це значення — лінія «їде» від курсора). Той самий клас
  // проблеми, що filter/position:fixed (2026-08-28). Панель / FAB / лінія —
  // сусіди .a11y-invert-scope, поза зумом.
  const z = ZOOM_LEVELS[s.zoom + 3];
  const scope = document.querySelector('.a11y-invert-scope');
  if (scope) scope.style.zoom = z !== 1 ? String(z) : '';
  document.body.style.zoom = '';   // прибрати можливе старе значення

  // data-атрибути
  root.dataset.a11yFont     = s.dyslexicFont  ? 'dyslexic' : '';
  root.dataset.a11yGray     = s.grayscale     ? '1' : '';
  root.dataset.a11yCursor   = s.largeCursor   ? 'large' : '';
  root.dataset.a11ySpacing  = String(s.letterSpacing);
  root.dataset.a11yInvert   = String(s.invertMode);

  // Кольори
  if (s.textColor) root.style.setProperty('--a11y-text', s.textColor);
  else             root.style.removeProperty('--a11y-text');
  if (s.bgColor)   root.style.setProperty('--a11y-bg', s.bgColor);
  else             root.style.removeProperty('--a11y-bg');
}

function clearApplied() {
  const root = document.documentElement;
  FONT_VARS.forEach(v => root.style.removeProperty(v));
  document.body.style.zoom = '';
  const scope = document.querySelector('.a11y-invert-scope');
  if (scope) scope.style.zoom = '';
  ['a11yFont','a11yGray','a11yCursor','a11ySpacing','a11yInvert']
    .forEach(k => { root.dataset[k] = ''; });
  root.style.removeProperty('--a11y-text');
  root.style.removeProperty('--a11y-bg');
}

const TEXT_SWATCHES = ['#1a1a2e','#c00000','#cc6600','#886600','#006600','#00008b','#6a0dad','#000000'];
const BG_SWATCHES   = ['#0a0a2e','#cceeff','#d4f0c0','#fffacd','#ffe4e1','#e8e0ff','#ffffff','#000000'];

// ── Секція "крокового" регулятора (+ / − / скинути) — той самий патерн
// повторювався окремо для розміру шрифту і масштабу сторінки.
function StepperSection({ title, value, min, max, onInc, onDec, onReset,
                           incIcon, decIcon, decIconStyle, incLabel, decLabel, resetLabel }) {
  return (
    <div className="a11y-section">
      <div className="a11y-section-title">{title}</div>
      <div className="a11y-row">
        <button className="a11y-btn" onClick={onInc} disabled={value >= max}>
          <i className={incIcon} /><span>{incLabel}</span>
        </button>
        <button className="a11y-btn" onClick={onDec} disabled={value <= min}>
          <i className={decIcon} style={decIconStyle} /><span>{decLabel}</span>
        </button>
        <button className="a11y-btn" onClick={onReset} disabled={value === 0}>
          <i className="fa-solid fa-rotate-left" /><span>{resetLabel}</span>
        </button>
      </div>
    </div>
  );
}

export default function AccessibilityPanel() {
  const [open,     setOpen]     = useState(false);
  const [settings, setSettings] = useState(load);
  const [tts,      setTts]      = useState('idle'); // idle | playing | paused
  const guideRef = useRef(null);

  useEffect(() => {
    applySettings(settings);
    try { localStorage.setItem(LS_KEY, JSON.stringify(settings)); } catch {}
  }, [settings]);

  // Лінія читання — слідує за мишею
  useEffect(() => {
    if (!settings.readingGuide) return;
    const handler = e => {
      if (guideRef.current) guideRef.current.style.top = e.clientY + 'px';
    };
    document.addEventListener('mousemove', handler);
    return () => document.removeEventListener('mousemove', handler);
  }, [settings.readingGuide]);

  // Зупинити TTS при виході
  useEffect(() => () => { window.speechSynthesis?.cancel(); }, []);

  const set = useCallback((key, val) =>
    setSettings(p => ({ ...p, [key]: val })), []);

  const resetAll = () => {
    window.speechSynthesis?.cancel();
    setTts('idle');
    clearApplied();
    setSettings({ ...DEFAULT });
    try { localStorage.removeItem(LS_KEY); } catch {}
  };

  // ── TTS ───────────────────────────────────────────
  const ttsPlay = () => {
    if (!window.speechSynthesis) return;
    if (tts === 'paused') { window.speechSynthesis.resume(); setTts('playing'); return; }
    window.speechSynthesis.cancel();
    const el = document.querySelector('[data-a11y-main]')
            || document.querySelector('main')
            || document.body;
    const text = el.innerText?.trim();
    if (!text) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'uk-UA';
    u.rate = 0.95;
    u.onend = u.onerror = () => setTts('idle');
    window.speechSynthesis.speak(u);
    setTts('playing');
  };
  const ttsPause = () => { window.speechSynthesis?.pause(); setTts('paused'); };
  const ttsStop  = () => { window.speechSynthesis?.cancel(); setTts('idle'); };

  const clampStep = (key, val, min, max) => set(key, Math.max(min, Math.min(max, val)));

  return (
    <>
      {/* Плаваюча кнопка */}
{!open && (
      <button className="a11y-fab" onClick={() => setOpen(true)}
        aria-label="Відкрити панель доступності" title="Опції доступності">
        <i className="fa-solid fa-universal-access" />
      </button>)}

      {/* Лінія читання */}
      {settings.readingGuide &&
        <div ref={guideRef} className="a11y-guide" aria-hidden="true" />}

      {/* Оверлей */}
      {open && <div className="a11y-overlay" onClick={() => setOpen(false)} />}

      {/* Панель */}
      <div className={`a11y-panel${open ? ' a11y-panel--open' : ''}`}
           role="dialog" aria-modal="true" aria-label="Опції доступності">

        <div className="a11y-header">
          <i className="fa-solid fa-universal-access me-2" />
          <span>Опції доступності</span>
          <button className="a11y-header-close" onClick={() => setOpen(false)} aria-label="Закрити">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="a11y-body">

          {/* 1. Озвучування */}
          <div className="a11y-section">
            <div className="a11y-section-title">Озвучування контенту</div>
            <div className="a11y-row">
              <button className={`a11y-btn${tts==='playing'?' a11y-btn--active':''}`} onClick={ttsPlay}>
                <i className="fa-solid fa-play" /><span>Відтворити</span>
              </button>
              <button className={`a11y-btn${tts==='paused'?' a11y-btn--active':''}`}
                      onClick={ttsPause} disabled={tts==='idle'}>
                <i className="fa-solid fa-pause" /><span>Пауза</span>
              </button>
              <button className="a11y-btn" onClick={ttsStop} disabled={tts==='idle'}>
                <i className="fa-solid fa-stop" /><span>Стоп</span>
              </button>
            </div>
          </div>

          {/* 2. Розмір шрифту */}
          <StepperSection
            title="Розмір шрифту" value={settings.fontSize} min={-2} max={3}
            onInc={() => clampStep('fontSize', settings.fontSize+1,-2,3)}
            onDec={() => clampStep('fontSize', settings.fontSize-1,-2,3)}
            onReset={() => set('fontSize',0)}
            incIcon="fa-solid fa-text-height" incLabel="Збільшити"
            decIcon="fa-solid fa-text-height" decIconStyle={{transform:'scaleY(-1)'}} decLabel="Зменшити"
            resetLabel="Скинути"
          />

          {/* 3. Додаткові опції */}
          <div className="a11y-section">
            <div className="a11y-section-title">Додаткові опції</div>
            <div className="a11y-grid">
              <button className={`a11y-btn${settings.dyslexicFont?' a11y-btn--on':''}`}
                      onClick={() => set('dyslexicFont', !settings.dyslexicFont)}>
                <i className="fa-solid fa-a" /><span>Дислексійний шрифт</span>
              </button>
              <button className={`a11y-btn${settings.grayscale?' a11y-btn--on':''}`}
                      onClick={() => set('grayscale', !settings.grayscale)}>
                <i className="fa-solid fa-droplet-slash" /><span>Відтінки сірого</span>
              </button>
              <button className={`a11y-btn${settings.largeCursor?' a11y-btn--on':''}`}
                      onClick={() => set('largeCursor', !settings.largeCursor)}>
                <i className="fa-solid fa-arrow-pointer" /><span>Великий курсор</span>
              </button>
              <button className={`a11y-btn${settings.readingGuide?' a11y-btn--on':''}`}
                      onClick={() => set('readingGuide', !settings.readingGuide)}>
                <i className="fa-solid fa-glasses" /><span>Лінія читання</span>
              </button>
              <button className="a11y-btn"
                      onClick={() => clampStep('letterSpacing', settings.letterSpacing+1, 0, 2)}
                      disabled={settings.letterSpacing>=2}>
                <i className="fa-solid fa-arrows-left-right-to-line" /><span>Збільшити інтервал між літерами</span>
              </button>
              <button className="a11y-btn"
                      onClick={() => clampStep('letterSpacing', settings.letterSpacing-1, 0, 2)}
                      disabled={settings.letterSpacing<=0}>
                <i className="fa-solid fa-left-right" /><span>Зменшити інтервал між літерами</span>
              </button>
            </div>
          </div>

          {/* 4. Масштабування */}
          <StepperSection
            title="Масштабування сторінки" value={settings.zoom} min={-3} max={3}
            onInc={() => clampStep('zoom', settings.zoom+1, -3, 3)}
            onDec={() => clampStep('zoom', settings.zoom-1, -3, 3)}
            onReset={() => set('zoom',0)}
            incIcon="fa-solid fa-magnifying-glass-plus" incLabel="Збільшити масштаб"
            decIcon="fa-solid fa-magnifying-glass-minus" decLabel="Зменшити масштаб"
            resetLabel="Скинути масштаб"
          />

          {/* 5. Інверсія */}
          <div className="a11y-section">
            <div className="a11y-section-title">Варіанти інверсії кольорів</div>
            <div className="a11y-row">
              {[1,2,3].map(m => (
                <button key={m}
                  className={`a11y-btn${settings.invertMode===m?' a11y-btn--on':''}`}
                  onClick={() => set('invertMode', settings.invertMode===m ? 0 : m)}>
                  <span className={`a11y-inv-icon a11y-inv-icon--${m}`} aria-hidden="true" />
                  <span>Інвертувати колір</span>
                </button>
              ))}
            </div>
          </div>

          {/* 6. Кольори */}
          <div className="a11y-section">
            <div className="a11y-section-title">Додаткові опції кольорів</div>
            <div className="a11y-colors">
              {[
                { label: 'Змінити колір тексту', key: 'textColor', swatches: TEXT_SWATCHES },
                { label: 'Змінити колір фону',   key: 'bgColor',   swatches: BG_SWATCHES  },
              ].map(({ label, key, swatches }) => (
                <div key={key} className="a11y-color-group">
                  <div className="a11y-color-label">{label}</div>
                  <div className="a11y-swatches">
                    {swatches.map(c => (
                      <button key={c}
                        className={`a11y-swatch${settings[key]===c?' a11y-swatch--on':''}`}
                        style={{ background: c }}
                        onClick={() => set(key, settings[key]===c ? '' : c)}
                        aria-label={c} />
                    ))}
                  </div>
                  {settings[key] &&
                    <button className="a11y-color-reset" onClick={() => set(key,'')}>Скинути</button>}
                </div>
              ))}
            </div>
          </div>

          {/* Скинути все */}
          <div className="a11y-section">
            <button className="a11y-reset-all" onClick={resetAll}>
              <i className="fa-solid fa-rotate-left me-2" />Скинути всі налаштування
            </button>
          </div>

        </div>
      </div>
    </>
  );
}